/**
 * 💰 Finance 도메인 특화 타입 정의
 *
 * Finance 도메인에서 사용하는 동적 필드에 대한 타입 안전한 접근을 제공
 *
 * 🎯 목적:
 * - `as unknown` 사용 제거
 * - 타입 안전성 보장
 * - Finance 도메인 특화 필드에 대한 명확한 타입 정의
 */

import type {
  UniversalBaseEntity,
  CustomField,
  ImpactField,
  DomainReferences,
} from '@engines/core/factories/UniversalBaseEntity';
import type { BusinessDomain } from '@engines/core/schemas/BusinessDomainTypes';

/** 자산 진화 Flow 페이로드 (AssetEvolutionEngine) */
export interface AssetEvolutionFlowPayload {
  flowType?: 'production' | 'purchase' | 'sale' | 'depreciation' | 'adjustment' | string;
  deltaQuantity: number;
  deltaAssetValue: number;
  experienceWeight?: number;
  qualityFactor?: number;
  marketFactor?: number;
  tenantId?: string;
  flowId?: string;
}

/** 자산 상태 스냅샷 (getCurrentAssetState 반환) */
export interface AssetStateSnapshot {
  quantity: number;
  assetValue: number;
  totalAssetValue: number;
}

// ============================================
// 🎯 Finance 기본 확장 타입
// ============================================

/**
 * Finance with CustomFields - CustomField 접근을 타입 안전하게
 */
export interface FinanceWithCustomFields extends UniversalBaseEntity {
  customFields: CustomField[];
}

/**
 * Finance with ValueStructure - ValueStructure 접근을 타입 안전하게
 */
export type FinanceWithValueStructure = Omit<UniversalBaseEntity, 'valueStructure'> & {
  valueStructure?: {
    financeAsset?: {
      value: number;
      totalValue: number;
      unit?: string;
      config?: {
        accountCode?: string;
        accountName?: string;
        normalBalance?: 'debit' | 'credit';
        financeCategory?: 'asset' | 'liability' | 'equity';
      };
    };
    revenue?: {
      amounts: Array<{
        id: string;
        revenueType: 'sales' | 'service' | 'interest' | 'rental' | 'other';
        total: number;
        currency: string;
        recognizedAt: Date;
        sourceFlowId?: string;
        sourceStreamId?: string;
        /** ✅ Level 1 계정 직접 거래 여부 */
        isDirectTransaction?: boolean;
        /** ✅ 하위 계정 코드 (isDirectTransaction이 false인 경우) */
        sourceAccountCode?: string;
      }>;
      /** ✅ Level 1 직접 거래 합계 */
      directTransactions: number;
      /** ✅ 하위 계정 합계 (aggregation으로 자동 계산) */
      childAccountSum: number;
      /** ✅ 최종 합계 (directTransactions + childAccountSum) */
      totalAmount: number;
      currency: string;
    };
    expense?: {
      amounts: Array<{
        id: string;
        expenseType: 'salary' | 'rent' | 'utilities' | 'material' | 'other';
        total: number;
        currency: string;
        incurredAt: Date;
        sourceFlowId?: string;
        sourceStreamId?: string;
        /** ✅ Level 1 계정 직접 거래 여부 */
        isDirectTransaction?: boolean;
        /** ✅ 하위 계정 코드 (isDirectTransaction이 false인 경우) */
        sourceAccountCode?: string;
      }>;
      /** ✅ Level 1 직접 거래 합계 */
      directTransactions: number;
      /** ✅ 하위 계정 합계 (aggregation으로 자동 계산) */
      childAccountSum: number;
      /** ✅ 최종 합계 (directTransactions + childAccountSum) */
      totalAmount: number;
      currency: string;
    };
  };
};

/**
 * Finance with DomainReferences - DomainReferences 접근을 타입 안전하게
 */
export interface FinanceWithDomainReferences extends UniversalBaseEntity {
  domainReferences: DomainReferences;
}

// ============================================
// 🎯 Finance State 특화 타입
// ============================================

/**
 * Finance State - 재무상태표 항목
 */
export type FinanceState = UniversalBaseEntity & {
  /** Finance 카테고리 */
  financeCategory?: 'asset' | 'liability' | 'equity';
  /** 계정 코드 */
  accountCode?: string;
  /** 계정 이름 */
  accountName?: string;
  /** 정상 잔액 */
  normalBalance?: 'debit' | 'credit';
  customFields?: CustomField[];
  impactFields?: ImpactField[];
  domainReferences?: DomainReferences;
  valueStructure?: FinanceWithValueStructure['valueStructure'];
};

// ============================================
// 🎯 Finance Flow 특화 타입
// ============================================

/**
 * Finance Flow - 손익계산서 항목
 */
export type FinanceFlow = UniversalBaseEntity & {
  /** Finance 카테고리 */
  financeCategory?: 'revenue' | 'expense';
  /** 계정 코드 */
  accountCode?: string;
  /** 계정 이름 */
  accountName?: string;
  /** 정상 잔액 */
  normalBalance?: 'debit' | 'credit';
  /** 회계연도 */
  fiscalYear?: number;
  /** 회계기간 (월) */
  fiscalPeriod?: number;
  customFields?: CustomField[];
  impactFields?: ImpactField[];
  domainReferences?: DomainReferences;
  valueStructure?: FinanceWithValueStructure['valueStructure'];
};

// ============================================
// 🎯 FlowEvent 특화 타입
// ============================================

/**
 * FlowEvent - Flow 이벤트 데이터
 */
export interface FlowEventData {
  id?: string;
  flowId?: string;
  name?: string;
  flowName?: string;
  description?: string;
  tenantId?: string;
  customFields?: CustomField[];
  eventData?: {
    valueFieldDeltas?: Array<{
      targetDomain: string;
      targetObjectId: string;
      valueFieldType: string;
      deltaAmount?: number;
      afterValue?: number;
      changeAmount?: number;
      fieldPath?: string;
    }>;
  };
  businessContext?: {
    totalBusinessValue?: number;
    relatedEntities?: string[];
  };
  valueFieldChanges?: Array<{
    fieldPath: string;
    changeAmount?: number;
    afterValue?: number;
  }>;
}

// ============================================
// 🎯 StreamEvent 특화 타입
// ============================================

/**
 * StreamEvent - Stream 이벤트 데이터
 */
/** revenue/expense amounts 배열 항목 */
export interface FinanceAmountLineItem {
  isDirectTransaction?: boolean;
  total?: number;
}

/** Net Income 집계 결과의 Flow 요약 행 */
export interface FinanceFlowNetIncomeLine {
  id: string;
  name: string;
  accountCode?: string;
  amount: number;
}

/** finance_flow ImpactField 정의 (동적 생성) */
export interface FinanceImpactFieldDef {
  key: string;
  label: string;
  impactType: string;
  value: number;
  aggregation?: Record<string, unknown>;
  [key: string]: unknown;
}

export interface StreamEventData {
  id?: string;
  streamId?: string;
  name?: string;
  streamName?: string;
  description?: string;
  tenantId?: string;
  executionSummary?: {
    totalValue?: number;
  };
  event_data?: {
    revenueAmount?: number;
    expenseAmount?: number;
    amount?: number;
  };
}

// ============================================
// 🎯 타입 가드 함수
// ============================================

/**
 * Finance State 타입 가드
 */
export function isFinanceState(
  entity: UniversalBaseEntity
): entity is UniversalBaseEntity & FinanceState {
  return (
    entity.domainCategory === 'finance' &&
    (entity.businessDomain === 'finance_state' ||
      getCustomFieldValue(entity, 'financeType') === 'state')
  );
}

/**
 * Finance Flow 타입 가드
 */
export function isFinanceFlow(
  entity: UniversalBaseEntity
): entity is UniversalBaseEntity & FinanceFlow {
  return (
    entity.domainCategory === 'finance' &&
    (entity.businessDomain === 'finance_flow' ||
      getCustomFieldValue(entity, 'financeType') === 'flow')
  );
}

/**
 * Finance with CustomFields 타입 가드
 */
export function hasFinanceCustomFields(
  entity: UniversalBaseEntity
): entity is FinanceWithCustomFields {
  return Array.isArray(entity.customFields);
}

/**
 * Finance with ValueStructure 타입 가드
 */
export function hasFinanceValueStructure(
  entity: UniversalBaseEntity
): entity is UniversalBaseEntity & FinanceWithValueStructure {
  return typeof entity.valueStructure === 'object' && entity.valueStructure !== null;
}

/**
 * Finance with DomainReferences 타입 가드
 */
export function hasFinanceDomainReferences(
  entity: UniversalBaseEntity
): entity is FinanceWithDomainReferences {
  return typeof entity.domainReferences === 'object' && entity.domainReferences !== null;
}

/**
 * FlowEvent 타입 가드
 */
export function isFlowEventData(data: unknown): data is FlowEventData {
  if (typeof data !== 'object' || data === null) return false;
  const event = data as Record<string, unknown>;
  return Boolean(
    (event.id || event.flowId) &&
    (event.eventData || event.valueFieldChanges || event.businessContext)
  );
}

/**
 * StreamEvent 타입 가드
 */
export function isStreamEventData(data: unknown): data is StreamEventData {
  if (typeof data !== 'object' || data === null) return false;
  const event = data as Record<string, unknown>;
  return Boolean((event.id || event.streamId) && (event.executionSummary || event.event_data));
}

// ============================================
// 🎯 헬퍼 함수 - 타입 안전한 필드 접근
// ============================================

/**
 * CustomField 값 조회 (타입 안전)
 */
export function getCustomFieldValue(entity: UniversalBaseEntity, key: string): unknown {
  if (!hasFinanceCustomFields(entity)) {
    return undefined;
  }

  const field = entity.customFields.find((f) => f.key === key);
  return field?.value;
}

/**
 * CustomField 값 설정 (타입 안전)
 */
export function setCustomFieldValue(
  entity: UniversalBaseEntity,
  key: string,
  value: unknown
): void {
  if (!hasFinanceCustomFields(entity)) {
    entity.customFields = [];
  }

  const customFields = entity.customFields;
  if (!customFields) {
    entity.customFields = [];
    return;
  }

  const field = customFields.find((f) => f.key === key);
  if (field) {
    field.value = value;
  } else {
    customFields.push({
      key,
      value,
      type: typeof value === 'number' ? 'number' : typeof value === 'boolean' ? 'boolean' : 'text',
    });
  }
}

/**
 * Finance Asset 값 조회 (타입 안전)
 */
export function getFinanceAssetValue(entity: UniversalBaseEntity): number {
  if (!hasFinanceValueStructure(entity)) {
    return 0;
  }

  return entity.valueStructure?.financeAsset?.value || 0;
}

/**
 * Finance Revenue 총액 조회 (타입 안전)
 */
export function getFinanceRevenueTotal(entity: UniversalBaseEntity): number {
  if (!hasFinanceValueStructure(entity)) {
    return 0;
  }

  return entity.valueStructure?.revenue?.totalAmount || 0;
}

/**
 * Finance Expense 총액 조회 (타입 안전)
 */
export function getFinanceExpenseTotal(entity: UniversalBaseEntity): number {
  if (!hasFinanceValueStructure(entity)) {
    return 0;
  }

  return entity.valueStructure?.expense?.totalAmount || 0;
}

/**
 * Finance State 정보 조회 (타입 안전)
 */
export function getFinanceStateInfo(entity: UniversalBaseEntity): {
  financeCategory?: 'asset' | 'liability' | 'equity';
  accountCode?: string;
  accountName?: string;
  normalBalance?: 'debit' | 'credit';
} | null {
  if (!isFinanceState(entity)) {
    return null;
  }

  const financeCategory = getCustomFieldValue(entity, 'financeCategory') as
    | 'asset'
    | 'liability'
    | 'equity'
    | undefined;
  const accountCode = getCustomFieldValue(entity, 'accountCode') as string | undefined;
  const accountName = getCustomFieldValue(entity, 'accountName') as string | undefined;
  const normalBalance = getCustomFieldValue(entity, 'normalBalance') as
    | 'debit'
    | 'credit'
    | undefined;

  return {
    ...(financeCategory !== undefined ? { financeCategory } : {}),
    ...(accountCode !== undefined ? { accountCode } : {}),
    ...(accountName !== undefined ? { accountName } : {}),
    ...(normalBalance !== undefined ? { normalBalance } : {}),
  };
}

/**
 * Finance Flow 정보 조회 (타입 안전)
 */
export function getFinanceFlowInfo(entity: UniversalBaseEntity): {
  financeCategory?: 'revenue' | 'expense';
  accountCode?: string;
  accountName?: string;
  normalBalance?: 'debit' | 'credit';
  fiscalYear?: number;
  fiscalPeriod?: number;
} | null {
  if (!isFinanceFlow(entity)) {
    return null;
  }

  const financeCategory = getCustomFieldValue(entity, 'financeCategory') as
    | 'revenue'
    | 'expense'
    | undefined;
  const accountCode = getCustomFieldValue(entity, 'accountCode') as string | undefined;
  const accountName = getCustomFieldValue(entity, 'accountName') as string | undefined;
  const normalBalance = getCustomFieldValue(entity, 'normalBalance') as
    | 'debit'
    | 'credit'
    | undefined;
  const fiscalYear = getCustomFieldValue(entity, 'fiscalYear') as number | undefined;
  const fiscalPeriod = getCustomFieldValue(entity, 'fiscalPeriod') as number | undefined;

  return {
    ...(financeCategory !== undefined ? { financeCategory } : {}),
    ...(accountCode !== undefined ? { accountCode } : {}),
    ...(accountName !== undefined ? { accountName } : {}),
    ...(normalBalance !== undefined ? { normalBalance } : {}),
    ...(fiscalYear !== undefined ? { fiscalYear } : {}),
    ...(fiscalPeriod !== undefined ? { fiscalPeriod } : {}),
  };
}

/**
 * FlowEvent에서 tenantId 조회 (타입 안전)
 */
export function getFlowEventTenantId(
  flowEvent: FlowEventData | Record<string, unknown> | { tenantId?: string }
): string | undefined {
  if (isFlowEventData(flowEvent)) {
    return flowEvent.tenantId;
  }
  if (typeof flowEvent.tenantId === 'string') {
    return flowEvent.tenantId;
  }
  return undefined;
}

/**
 * FlowEvent에서 ID 조회 (타입 안전)
 */
export function getFlowEventId(
  flowEvent: FlowEventData | Record<string, unknown>
): string | undefined {
  if (isFlowEventData(flowEvent)) {
    return flowEvent.id || flowEvent.flowId;
  }
  // 타입 가드 실패 시 안전하게 접근
  const event = flowEvent as Record<string, unknown>;
  const id = event.id ?? event.flowId;
  return typeof id === 'string' ? id : undefined;
}

/**
 * FlowEvent에서 이름 조회 (타입 안전)
 */
export function getFlowEventName(
  flowEvent: FlowEventData | Record<string, unknown>
): string | undefined {
  if (isFlowEventData(flowEvent)) {
    return flowEvent.name || flowEvent.flowName;
  }
  // 타입 가드 실패 시 안전하게 접근
  const event = flowEvent as Record<string, unknown>;
  const name = event.name ?? event.flowName;
  return typeof name === 'string' ? name : undefined;
}

/**
 * FlowEvent에서 설명 조회 (타입 안전)
 */
export function getFlowEventDescription(
  flowEvent: FlowEventData | Record<string, unknown>
): string | undefined {
  if (isFlowEventData(flowEvent)) {
    return flowEvent.description;
  }
  // 타입 가드 실패 시 안전하게 접근
  const event = flowEvent as Record<string, unknown>;
  return typeof event.description === 'string' ? event.description : undefined;
}

/**
 * FlowEvent에서 businessContext.relatedEntities 조회 (타입 안전)
 */
export function getFlowEventRelatedEntities(
  flowEvent: FlowEventData | Record<string, unknown>
): string[] | undefined {
  if (isFlowEventData(flowEvent)) {
    return flowEvent.businessContext?.relatedEntities;
  }
  // 타입 가드 실패 시 안전하게 접근
  const event = flowEvent as Record<string, unknown>;
  const ctx = event.businessContext;
  if (ctx && typeof ctx === 'object' && ctx !== null && 'relatedEntities' in ctx) {
    const related = (ctx as { relatedEntities?: unknown }).relatedEntities;
    return Array.isArray(related) ? (related as string[]) : undefined;
  }
  return undefined;
}

/**
 * StreamEvent에서 ID 조회 (타입 안전)
 */
export function getStreamEventId(
  streamEvent: StreamEventData | Record<string, unknown>
): string | undefined {
  if (isStreamEventData(streamEvent)) {
    return streamEvent.id || streamEvent.streamId;
  }
  // 타입 가드 실패 시 안전하게 접근
  const event = streamEvent as Record<string, unknown>;
  const id = event.id ?? event.streamId;
  return typeof id === 'string' ? id : undefined;
}

/**
 * StreamEvent에서 이름 조회 (타입 안전)
 */
export function getStreamEventName(
  streamEvent: StreamEventData | Record<string, unknown>
): string | undefined {
  if (isStreamEventData(streamEvent)) {
    return streamEvent.name || streamEvent.streamName;
  }
  // 타입 가드 실패 시 안전하게 접근
  const event = streamEvent as Record<string, unknown>;
  const name = event.name ?? event.streamName;
  return typeof name === 'string' ? name : undefined;
}

/**
 * StreamEvent에서 설명 조회 (타입 안전)
 */
export function getStreamEventDescription(
  streamEvent: StreamEventData | Record<string, unknown>
): string | undefined {
  if (isStreamEventData(streamEvent)) {
    return streamEvent.description;
  }
  // 타입 가드 실패 시 안전하게 접근
  const event = streamEvent as Record<string, unknown>;
  return typeof event.description === 'string' ? event.description : undefined;
}

/**
 * BusinessDomain 타입 안전 변환
 */
export function toBusinessDomain(value: string): BusinessDomain | null {
  // BusinessDomain 타입에 포함되는지 검증
  const validDomains: BusinessDomain[] = [
    'internal_entity',
    'external_entity',
    'user_entity',
    'sellable_item',
    'service_item',
    'asset_item',
    'consumable_item',
    'raw_material_item',
    'finance_state',
    'finance_flow',
    'physical_space',
    'virtual_space',
    'knowledge_report',
    'knowledge_policy',
    'knowledge_plan',
    'semantic_formation',
    'ephemeral_formation',
    'ephemeral_performance',
    'recurrent_performance',
    'calendar_anchor',
    'calendar_cycle',
    'calendar_trigger',
    'flow_definition',
    'stream_definition',
    'flow_execution',
    'stream_execution',
  ];

  if (validDomains.includes(value as BusinessDomain)) {
    return value as BusinessDomain;
  }

  return null;
}
